/**
 * Shared, batched resolver cache for read-only RECORD_REFERENCE display labels
 * (id -> name), keyed by (resource, id).
 *
 * Read-only reference cells in record tables / dashboard widgets used to each
 * mount their own `useAsyncSelect` with a unique iri, firing one single-id
 * `{id eq value}` resolution fetch per cell and allocating one redux slice per
 * cell. On a busy screen (e.g. the operator dashboard, ~576 reference cells)
 * that is hundreds of independent id->name fetches and hundreds of slices.
 *
 * This slice deduplicates by (resource, id): the first cell to need an id marks
 * it `loading`; a batching buffer (ReferenceLabel.service) collapses every
 * pending id for a resource into ONE `{id in [...]}` query, and the resolved
 * names land here once, shared by every cell referencing the same record.
 *
 * Entry shape: { status: "loading" | "resolved", label: string | null }.
 * `label === null` on a resolved entry means the record was not found (e.g.
 * permanently deleted); the cell then falls back to rendering the raw id, the
 * same graceful degradation the per-cell resolver had.
 */
export const REQUEST_REFERENCE_LABELS = "REQUEST_REFERENCE_LABELS";
export const STORE_REFERENCE_LABELS = "STORE_REFERENCE_LABELS";

export const initialState = {}; // { [resource]: { [id]: { status, label } } }

const referenceLabelReducer = (state = initialState, action = {}) => {
    switch (action.type) {
        case REQUEST_REFERENCE_LABELS: {
            const { resource, ids } = action;
            const existing = state[resource] || {};
            let changed = false;
            const next = { ...existing };
            (ids || []).forEach((id) => {
                if (next[id] === undefined) {
                    next[id] = { status: "loading", label: null };
                    changed = true;
                }
            });
            // Preserve the state reference when nothing new is marked loading so
            // per-cell useSelector subscriptions do not re-render needlessly.
            return changed ? { ...state, [resource]: next } : state;
        }
        case STORE_REFERENCE_LABELS: {
            const { resource, ids, labels = {} } = action;
            const existing = state[resource] || {};
            const next = { ...existing };
            (ids || []).forEach((id) => {
                next[id] = {
                    status: "resolved",
                    label: labels[id] != null ? labels[id] : null,
                };
            });
            return { ...state, [resource]: next };
        }
        default:
            return state;
    }
};

export default referenceLabelReducer;
